import React, { useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useCompliance } from '../../contexts/ComplianceContext';
import WizardProgress from '../../components/compliance/WizardProgress';
import ProjectSetupStep from './ProjectSetupStep';
import ConnectIntegrationsStep from './ConnectIntegrationsStep';
import InitialScanStep from './InitialScanStep';
import RiskAssessmentStep from './RiskAssessmentStep';
import { ArrowLeft, X } from 'lucide-react';

const ComplianceOnboardingWizard = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const {
    currentStep,
    projectId,
    restoreFromParams,
    resetWizard,
    goBack,
    fetchIntegrationStatus,
    awsSSOState,
    cancelAWSSSO,
  } = useCompliance();

  // Restore state from URL parameters (for OAuth callbacks)
  useEffect(() => {
    restoreFromParams(searchParams);
  }, []);

  // Handle OAuth callback parameters
  useEffect(() => {
    const integration = searchParams.get('integration');
    const success = searchParams.get('success');
    const error = searchParams.get('error');

    if (integration && (success || error)) {
      // Clear URL params
      searchParams.delete('integration');
      searchParams.delete('success');
      searchParams.delete('error');
      setSearchParams(searchParams);

      // Refresh integration status
      if (projectId) {
        fetchIntegrationStatus();
      }
    }
  }, [searchParams, projectId, fetchIntegrationStatus, setSearchParams]);

  // Cleanup AWS SSO polling when component unmounts
  useEffect(() => {
    return () => {
      // Cancel AWS SSO polling if it's active when the component unmounts
      if (awsSSOState.isPolling) {
        cancelAWSSSO();
      }
    };
  }, [awsSSOState.isPolling, cancelAWSSSO]);

  // Handle cancel/close wizard
  const handleCancel = () => {
    if (window.confirm('Are you sure you want to cancel? Your progress will be lost.')) {
      resetWizard();
      navigate('/repositories');
    }
  };

  // Handle back navigation
  const handleBack = () => {
    if (currentStep === 1) {
      navigate('/repositories');
    } else {
      goBack();
    }
  };

  // Render current step
  const renderStep = () => {
    switch (currentStep) {
      case 1:
        return <ProjectSetupStep />;
      case 2:
        return <ConnectIntegrationsStep />;
      case 3:
        return <InitialScanStep />;
      case 4:
        return <RiskAssessmentStep />;
      default:
        return <ProjectSetupStep />;
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      {/* `top-14` clears SidebarLayout's mobile top bar; desktop has none. */}
      <div className="bg-white border-b border-gray-200 sticky top-14 md:top-0 z-10">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button
                onClick={handleBack}
                className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                title="Go back"
              >
                <ArrowLeft className="w-5 h-5" />
              </button>
              <div>
                <h1 className="text-xl font-semibold text-gray-900">
                  New Compliance Project
                </h1>
                <p className="text-sm text-gray-500">
                  Set up SOC 2 compliance tracking for your organization
                </p>
              </div>
            </div>

            <button
              onClick={handleCancel}
              className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
              title="Cancel"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>
      </div>

      {/* Progress Bar */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-4xl mx-auto px-4 sm:px-6">
          <WizardProgress currentStep={currentStep} />
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-6 sm:py-8">
        {renderStep()}
      </div>
    </div>
  );
};

export default ComplianceOnboardingWizard;
